/* W11-GOV — Corpus documentaire + boucle de maintien (P-3) :
 * changement de document → impact détecté → responsables → obligation de re-validation. */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { FileText, RefreshCw, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";

function RescanForm({ doc, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const rescan = async () => {
    if (text.trim().length < 80) return notify.error("Collez la nouvelle version du document (≥ 80 caractères).");
    setBusy(true);
    try {
      const r = await api.post(`/workspace/gov-intake/documents/${doc.id}/rescan`, { text: text.trim() });
      setReport(r.data);
      notify.success(r.data.changed
        ? `Impact analysé : ${r.data.affected.length} objet(s) affecté(s), ${r.data.obligations_created} obligation(s) créée(s).`
        : "Aucun changement de contenu détecté.");
      onDone();
    } catch (e) { notify.error(fmtError(e)); }
    setBusy(false);
  };
  return (
    <div className="mt-2 space-y-1.5">
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4}
        placeholder="Collez ici la NOUVELLE version du document — NOAM détecte ce qui casse dans votre registre…"
        className="noam-input text-xs w-full" data-testid={`gov-doc-rescan-text-${doc.id}`} />
      <button onClick={rescan} disabled={busy} className="btn-primary text-[10px]" data-testid={`gov-doc-rescan-btn-${doc.id}`}>
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Analyser l'impact sur le registre
      </button>
      {report && (
        <div className="p-2 rounded bg-white/[0.03] border border-white/5 text-[10px] space-y-1" data-testid={`gov-doc-impact-${doc.id}`}>
          <div className="text-zinc-300">Version {report.version} — {report.note}</div>
          <div className="flex gap-3">
            <span className="text-[#1D9E75] flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {report.intact.length} intact(s)</span>
            <span className="text-[#EF9F27] flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {report.affected.length} affecté(s)</span>
            <span className="text-zinc-400">{report.obligations_created} obligation(s) de re-validation créée(s)</span>
          </div>
          {report.affected.map((a) => (
            <div key={a.proposal_id} className="text-zinc-400">
              → {a.title} {a.materialized?.code ? `(${a.materialized.code})` : ""} — l'extrait source a disparu ou changé
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function GovDocumentsPanel() {
  const [docs, setDocs] = useState(null);
  const [openId, setOpenId] = useState(null);
  const load = () => api.get("/workspace/gov-intake/documents")
    .then((r) => setDocs(r.data.items)).catch((e) => notify.error(fmtError(e)));
  useEffect(() => { load(); }, []);

  if (!docs) return <div className="flex justify-center py-8 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /></div>;
  return (
    <div className="space-y-2" data-testid="gov-documents-panel">
      <div className="text-[10px] px-2.5 py-1.5 rounded border border-white/10 text-zinc-400 bg-white/[0.02]">
        Chaque source analysée est conservée et versionnée. Quand un document change, NOAM détecte
        les objets du registre qui ne sont plus fondés — et crée une obligation de re-validation
        pour le responsable. Rien n'est modifié automatiquement.
      </div>
      {docs.length === 0 && (
        <div className="p-8 text-center text-xs text-zinc-600 border border-dashed border-white/10 rounded-lg" data-testid="gov-documents-empty">
          Aucune source analysée. Le corpus se construit via l'onglet Intake.
        </div>
      )}
      {docs.map((d) => (
        <div key={d.id} className="noam-card p-3" data-testid={`gov-doc-${d.id}`}>
          <div className="flex items-center gap-2 text-xs flex-wrap">
            <FileText className="w-3.5 h-3.5 text-[#7F77DD]" />
            <span className="text-zinc-200 flex-1 min-w-[180px]">{d.label}</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded-full border border-white/15 text-zinc-400">v{d.version}</span>
            <span className="text-[10px] text-zinc-500">{d.accepted_count} objet(s) au registre</span>
            <button onClick={() => setOpenId(openId === d.id ? null : d.id)} className="btn-ghost text-[10px] border border-white/10"
              data-testid={`gov-doc-open-${d.id}`}>
              <RefreshCw className="w-3 h-3" /> Nouvelle version ?
            </button>
          </div>
          <div className="text-[10px] text-zinc-600 mt-1">
            Analysé le {new Date(d.analyzed_at).toLocaleDateString("fr-CH")} par {d.created_by_email} · {d.chars || "—"} caractères
          </div>
          {openId === d.id && <RescanForm doc={d} onDone={load} />}
        </div>
      ))}
    </div>
  );
}
